import { z } from 'zod';
import type { PendingChatSubmission } from '../chat/models/pending-chat-submission';
import { getChatSeedStrategy, listChatSeedStrategies } from './registry';
import { SHARED_CHAT_SEED_QUERY_KEYS, type ChatSeedBuildInput } from './types';

const uuidSchema = z.string().uuid();

function allStripQueryKeys(): string[] {
  const extra = listChatSeedStrategies().flatMap(
    (strategy) => strategy.extraStripQueryKeys ?? []
  );
  return [...SHARED_CHAT_SEED_QUERY_KEYS, ...extra];
}

export function buildChatSeedSearchParams(
  type: string,
  input: ChatSeedBuildInput
): string {
  const strategy = getChatSeedStrategy(type);
  if (strategy === undefined) {
    throw new Error(`Unknown chat seed type: ${type}`);
  }
  return strategy.buildUrlSearchParams(input).toString();
}

export function hasChatSeedInSearchParams(
  searchParams: URLSearchParams
): boolean {
  const seed = searchParams.get('seed');
  if (seed === null) {
    return false;
  }
  return getChatSeedStrategy(seed) !== undefined;
}

/** True while the chat is a seed shell (URL/deferred seed, no messages yet). */
export function isChatSeedActive(params: {
  searchParams: URLSearchParams;
  hasDeferredSeedContext: boolean;
  messageCount: number;
}): boolean {
  if (params.messageCount > 0) {
    return false;
  }
  return (
    hasChatSeedInSearchParams(params.searchParams) ||
    params.hasDeferredSeedContext === true
  );
}

export function parseChatSeedFolderId(
  searchParams: URLSearchParams
): string | undefined {
  const folderId = searchParams.get('folderId');
  if (folderId === null) {
    return undefined;
  }
  const parsed = uuidSchema.safeParse(folderId);
  return parsed.success ? parsed.data : undefined;
}

/**
 * Seed keys that only hold while the seeded artifact is still unsaved: the `new`
 * marker plus whatever new-only params the strategy encodes (e.g. the workflow's
 * seed trigger). They must be cleared the moment the artifact is persisted —
 * otherwise coming back to this URL (browser back, reload, reopening the chat)
 * seeds another blank draft instead of showing the resource that was just saved.
 * The rest of the seed stays: it is what reopens the artifact in the panel.
 *
 * Empty when this URL is not a live `new` seed for `artifactId`.
 */
export function chatSeedKeysToClearOnPersist(
  searchParams: URLSearchParams,
  artifactId: string
): string[] {
  if (searchParams.get('new') !== '1') {
    return [];
  }
  if (searchParams.get('artifactId') !== artifactId) {
    return [];
  }
  const seed = searchParams.get('seed');
  const strategy = seed === null ? undefined : getChatSeedStrategy(seed);
  if (strategy === undefined) {
    return [];
  }
  return ['new', ...(strategy.extraStripQueryKeys ?? [])];
}

export function stripChatSeedSearchParams(
  searchParams: URLSearchParams
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  if (!hasChatSeedInSearchParams(searchParams)) {
    return next;
  }
  for (const key of allStripQueryKeys()) {
    next.delete(key);
  }
  return next;
}

export function parseChatSeedFromSearchParams(
  searchParams: URLSearchParams
): PendingChatSubmission | null {
  const seed = searchParams.get('seed');
  if (seed === null) {
    return null;
  }

  const strategy = getChatSeedStrategy(seed);
  if (strategy === undefined) {
    return null;
  }

  return strategy.parseFromUrl(searchParams);
}
